import { useMemo,useRef } from 'react'
import { useFrame,useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { useFightStore, type ImpactCue } from './store'
import { impactFeedbackFrameDelta, impactFeedbackProgress } from './impactFeedbackMath'
import { resolveImpactVisualProfile, type ImpactFragmentShape, type ImpactVisualProfile } from './impactVisualProfile'
import { applyVfxRuntimePolicy,browserVfxCaps } from './vfxRuntimePolicy'
import { PerceptualMorphogenesisFieldRuntime } from '@inyron/animation'

function FragmentGeometry({shape,size}:{shape:ImpactFragmentShape;size:number}){
 if(shape==='spark')return <tetrahedronGeometry args={[size,0]}/>
 if(shape==='shard')return <octahedronGeometry args={[size,0]}/>
 if(shape==='droplet')return <sphereGeometry args={[size,7,6]}/>
 if(shape==='leaf')return <coneGeometry args={[size*.7,size*1.9,5]}/>
 return <boxGeometry args={[size*1.35,size*.7,size]}/>
}

function CoreGeometry({cue}:{cue:ImpactCue}){
 if(cue.kind==='block')return <boxGeometry args={[.15,1.1,.85]}/>
 if(cue.kind==='throw')return <octahedronGeometry args={[.3,0]}/>
 return <sphereGeometry args={[.22,10,10]}/>
}

function MaterialBurst({visual,serial,direction}:{visual:ImpactVisualProfile;serial:number;direction:number}){
 const phase=(serial%19)*.173
 return <group>{Array.from({length:visual.fragmentCount},(_,i)=>{
  const angle=phase+i*2.399963229728653,radial=.4+(i%3)*.11,rise=((i%4)-1.5)*.12
  return <mesh key={serial+'-material-'+i} position={[direction*(.16+Math.cos(angle)*radial),rise,Math.sin(angle)*radial*.48]} rotation={[angle*.43,angle,angle*1.31]}>
   <FragmentGeometry shape={visual.fragmentShape} size={visual.fragmentSize}/>
   <meshBasicMaterial color={i%3===0?'#ffffff':visual.fragmentColor} transparent opacity={.82} blending={THREE.AdditiveBlending} depthWrite={false}/>
  </mesh>
 })}</group>
}

function StageAftermath({visual,height}:{visual:ImpactVisualProfile;height:number}){
 if(visual.floorOpacity<=0)return null
 return <group position={[0,-height+.025,0]}>
  <mesh rotation={[-Math.PI/2,0,0]}>
   <ringGeometry args={[visual.floorRadius*.72,visual.floorRadius,40]}/>
   <meshBasicMaterial color={visual.secondary} transparent opacity={visual.floorOpacity} blending={THREE.AdditiveBlending} depthWrite={false}/>
  </mesh>
  {Array.from({length:visual.breakRays},(_,i)=>{
   const angle=i/Math.max(visual.breakRays,1)*Math.PI*2+(i%2)*.21,radius=visual.floorRadius*.46
   return <mesh key={'crack-'+i} position={[Math.cos(angle)*radius,.006,Math.sin(angle)*radius]} rotation={[0,-angle,0]}>
    <boxGeometry args={[visual.floorRadius*.5,.012,.025]}/>
    <meshBasicMaterial color={visual.fragmentColor} transparent opacity={visual.floorOpacity*.82} blending={THREE.AdditiveBlending} depthWrite={false}/>
   </mesh>
  })}
 </group>
}

export function ImpactFeedback(){
 const {scene}=useThree(),healthTimer=useRef(0)
 const cue=useFightStore(s=>s.impactCue)
 const baseCaps=useMemo(()=>browserVfxCaps(),[])
 const perceptualField=useMemo(()=>new PerceptualMorphogenesisFieldRuntime('fight:impact-feedback','high'),[])
 const runtimePressure=typeof window==='undefined'?undefined:(window as typeof window&{__INYRON_RUNTIME_PERFORMANCE__?:{pressure?:'normal'|'high'|'critical'}}).__INYRON_RUNTIME_PERFORMANCE__?.pressure
 const pressure01=runtimePressure==='critical'?1:runtimePressure==='high'?.62:0
 const root=useRef<THREE.Group>(null),ring=useRef<THREE.Mesh>(null),core=useRef<THREE.Mesh>(null),burst=useRef<THREE.Group>(null)
 const perceptual=cue?perceptualField.sample(cue.serial*.173,{
  salience01:cue.kind==='parry'||cue.kind==='guard-break'?1:cue.kind==='throw'?.82:.68,
  motion01:cue.kind==='throw'?.9:.72,
  contrast01:.86,
  surfaceComplexity01:.72,
  runtimePressure01:pressure01,
  reducedMotion:baseCaps.reducedMotion,
 }):undefined
 const visual=cue?applyVfxRuntimePolicy(resolveImpactVisualProfile(cue),cue.kind,{...baseCaps,runtimePressure},perceptual):null
 useFrame((_,dt)=>{
  healthTimer.current+=Math.max(0,dt)
  if(healthTimer.current>=.5){healthTimer.current=0;scene.userData.inyronImpactVfx={active:true,pressure:runtimePressure??'normal',caps:baseCaps,at:performance.now()}}
  if(!cue||!visual||!root.current)return
  const safeDt=impactFeedbackFrameDelta(dt)
  const remaining=Math.max(0,useFightStore.getState().impactCueTimer)
  const p=impactFeedbackProgress(remaining,visual.duration),direction=cue.source==='player'?1:-1
  root.current.position.set(cue.x,cue.y??visual.height,cue.z)
  root.current.rotation.z=direction*visual.rotationZ
  root.current.scale.setScalar(visual.baseScale+p*visual.growth)
  if(ring.current){
   const material=ring.current.material as THREE.MeshBasicMaterial
   material.opacity=Math.max(0,(1-p)*visual.ringOpacity)
   ring.current.rotation.z+=safeDt*visual.spin*direction
  }
  if(core.current)(core.current.material as THREE.MeshBasicMaterial).opacity=Math.max(0,(1-p)*visual.coreOpacity)
  if(burst.current)burst.current.rotation.z+=safeDt*visual.fragmentSpin*.32
 })
 if(!cue||!visual)return null
 const direction=cue.source==='player'?1:-1
 const coreScale=cue.kind==='guard-break'?[1.2,.62,.62]:cue.kind==='block'?[.3,1.05,.72]:cue.kind==='throw'?[.9,.42,.68]:[.62,.62,.62]
 return <group ref={root} position={[cue.x,cue.y??visual.height,cue.z]}>
  <mesh ref={core} scale={coreScale as [number,number,number]}>
   <CoreGeometry cue={cue}/>
   <meshBasicMaterial color={visual.primary} transparent opacity={visual.coreOpacity} blending={THREE.AdditiveBlending} depthWrite={false}/>
  </mesh>
  <mesh ref={ring} rotation={[Math.PI/2,0,0]}>
   <ringGeometry args={[.28,cue.kind==='guard-break'?.56:cue.kind==='throw'?.5:.42,cue.kind==='guard-break'?12:cue.kind==='throw'?10:24]}/>
   <meshBasicMaterial color={visual.primary} transparent opacity={visual.ringOpacity} blending={THREE.AdditiveBlending} depthWrite={false} side={THREE.DoubleSide}/>
  </mesh>
  <group ref={burst}><MaterialBurst visual={visual} serial={cue.serial} direction={direction}/></group>
  {visual.directionalLength>0&&<mesh position={[direction*visual.directionalLength*.48,0,0]} rotation={[0,0,direction*Math.PI/2]}>
   <coneGeometry args={[.13,visual.directionalLength,7,1,true]}/>
   <meshBasicMaterial color={visual.secondary} transparent opacity={.48} blending={THREE.AdditiveBlending} depthWrite={false} side={THREE.DoubleSide}/>
  </mesh>}
  {cue.kind==='throw'&&<mesh position={[direction*.2,-.28,0]} rotation={[-Math.PI/2,0,0]}>
   <ringGeometry args={[.2,.42,12]}/>
   <meshBasicMaterial color={visual.secondary} transparent opacity={.58} blending={THREE.AdditiveBlending} depthWrite={false}/>
  </mesh>}
  <StageAftermath visual={visual} height={cue.y??visual.height}/>
  <pointLight color={visual.primary} intensity={visual.lightIntensity} distance={visual.lightDistance} decay={2}/>
 </group>
}
